import { useMemo } from "react";

import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { ClickToDialViewMode } from "@/Common/Components/CallCenter/ClickToDial.types";
import { ClickToDialView } from "@/Common/Components/CallCenter/ClickToDialView";
import { ElementWithIcon } from "@/Common/Components/ElementWithIcon";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { ContactDirectoryHelper } from "@/ContactDirectory/Helpers/ContactDirectoryHelper";
import { DirectorySearchHelper } from "@/ContactDirectory/Helpers/DirectorySearchHelper";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { usePhoneNumberFormatter } from "@/ContactDirectory/Hooks/usePhoneNumberFormatter";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models//DirectorySelectableEntity";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models/DirectoryFaxNumber";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { InvalidPharmacyWarningMessage } from "../Common/InvalidPharmacyWarningMessage";

import commonStyles from "../FaxDirectoryForm.module.scss";
import styles from "./FaxDirectorySelectedEntityComponent.module.scss";

interface IFaxDirectorySelectedSimpleComponentProps {
    entity: DirectorySelectableEntity<IDirectoryEntity>;
    selectedNumberId: string;
    setSelectedNumberId: (faxNumberId: string) => void;
    close: () => void;
    patientContactId?: string;
}

function FaxComponent(props: { fax: DirectoryFaxNumber }): JSX.Element {
    const { getText } = useTranslation();
    const formatter = usePhoneNumberFormatter(props.fax.countryId);
    const formattedFax = useMemo(
        () => formatter(props.fax),
        [props.fax, formatter]
    );
    return (
        <div>
            <div className="ellipsis-auto" title={formattedFax}>
                {formattedFax}
            </div>
            <div
                className={`${styles.faxDescription} italicFont ellipsis-auto`}
                title={props.fax.description}
            >
                {props.fax.description}
            </div>
            {props.fax.isObsoleteFaxNumberLink && (
                <div className={commonStyles.dangerColor}>
                    {getText("ContactDirectory_OutdatedFaxNumberLinkWarning")}
                </div>
            )}
        </div>
    );
}

function FaxDirectorySelectedSimpleComponent(props: IFaxDirectorySelectedSimpleComponentProps): JSX.Element {
    const { getText } = useTranslation();

    const faxNumberElements = useMemo(
        () => props.entity.faxInfos
            ?.filter(p => PhoneNumberHelper.validate(p?.faxNumber?.number, p?.faxNumber?.countryId))
            .map(faxInfo => new InlineOption<string>(faxInfo.faxNumber.id, <FaxComponent fax={faxInfo.faxNumber} />))
            ?? [],
        [props.entity.faxInfos]
    );

    const address = useMemo(
        () => props.entity.address,
        [props.entity.address]
    );

    const phoneNumbersTitle = useMemo(
        () => DirectorySearchHelper.formatPhoneNumbers(props.entity.phoneNumbers.map(p => PhoneNumberHelper.formatDirectoryNumber(p))),
        [props.entity.phoneNumbers]
    );

    const phoneNumbers = useMemo(
        () => {
            const callCenterFeatureFlag: boolean = cacheStore.ClinicSettings.IsCallCenterEnabled();
            if (callCenterFeatureFlag) {
                return DirectorySearchHelper.formatComponentPhoneNumbers(props.entity.phoneNumbers.map(p =>
                    <ClickToDialView key={p.id} viewMode={ClickToDialViewMode.Hyperlink} formattedPhoneNumber={PhoneNumberHelper.formatDirectoryNumber(p)} patientContactId={GuidHelper.AsGuid(props.patientContactId)} />
                ));
            }
            else {
                return <>{phoneNumbersTitle}</>;
            }
        },
        [props.entity.phoneNumbers, props.patientContactId, phoneNumbersTitle]
    );

    const entityDisplayName = props.entity.name;

    if (!props.entity)
        return null;

    return (
        <div>
            <div className={`flex-container ${styles.selectedEntity}`}>
                <ElementWithIcon
                    includeIcon={cacheStore.validateMylePharmacy(ContactDirectoryHelper.isMylePharmacy(props.entity.model))}
                    dataCy="fax-directory-selected-simple-component-entity-icon"
                >
                    <span
                        className={styles.displayName}
                        title={entityDisplayName}
                    >
                        {entityDisplayName}
                    </span>
                </ElementWithIcon>
                <RemoveButton onRemove={() => props.close()} className={styles.closeButton} />
            </div>

            <div className="ellipsis-auto" title={address}>{address}</div>
            <div className="ellipsis-auto" title={phoneNumbersTitle}>{phoneNumbers}</div>
            {faxNumberElements?.any() && (
                <>
                    <div className={styles.faxNumberLabel}>{getText("ContactDirectory_FaxNumberLabel")}</div>
                    <InlineOptionsListControl
                        id="selectFax"
                        items={faxNumberElements}
                        onChange={item => props.setSelectedNumberId(item.Key)}
                        value={props.selectedNumberId}
                        style={{ marginTop: "6px", alignItems: "flex-start", flexGrow: 1 }}
                        inputStyle={{ marginTop: "3px" }}
                        isMultipleLines={true}
                        labelStyle={{ width: "100%" }}
                    />
                </>
            )}
            {props.entity?.isInvalid &&
                <InvalidPharmacyWarningMessage />
            }
        </div>
    );
}

export { FaxComponent, FaxDirectorySelectedSimpleComponent };
